import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { adminService } from '../../services/adminService'

const sections = [
  { key: 'licenses', label: 'Licencias', path: '/admin/licenses' },
  { key: 'users', label: 'Usuarios', path: '/admin/users' },
  { key: 'schema', label: 'Schema', path: '/admin/schema' },
  { key: 'categories', label: 'Categorias', path: '/admin/categories' },
  { key: 'products', label: 'Productos', path: '/admin/products' },
  { key: 'landingPages', label: 'Landing Pages', path: '/admin/landing-pages' },
  { key: 'landingLeads', label: 'Leads Landing', path: '/admin/landing-leads' },
  { key: 'landingData', label: 'Tracking Landing', path: '/admin/landing-data' },
  { key: 'productionBackup', label: 'Backup producción', path: '/admin/production-backup' }
]

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  return date.toISOString().split('T')[0]
}

const toDateTimeLabel = (value) => {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

const createEmptyPaged = () => ({
  page: 1,
  pageSize: 20,
  totalItems: 0,
  items: []
})

const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1)

const resolveSection = (pathname) => {
  if (pathname.includes('/admin/users')) return 'users'
  if (pathname.includes('/admin/schema')) return 'schema'
  if (pathname.includes('/admin/categories')) return 'categories'
  if (pathname.includes('/admin/products')) return 'products'
  if (pathname.includes('/admin/landing-pages')) return 'landingPages'
  if (pathname.includes('/admin/landing-leads')) return 'landingLeads'
  if (pathname.includes('/admin/landing-data')) return 'landingData'
  if (pathname.includes('/admin/production-backup')) return 'productionBackup'
  return 'licenses'
}

export default function AdminModule() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [products, setProducts] = useState([])
  const [licenses, setLicenses] = useState([])
  const [pendingLicenseRequests, setPendingLicenseRequests] = useState([])
  const [approvingRequestId, setApprovingRequestId] = useState(null)
  const [provisioningRentalBizUserId, setProvisioningRentalBizUserId] = useState(null)
  const [schemaStatus, setSchemaStatus] = useState([])
  const [categories, setCategories] = useState([])
  const [catalogProducts, setCatalogProducts] = useState([])
  const [licenseFilter, setLicenseFilter] = useState('all')
  const [licenseSearch, setLicenseSearch] = useState('')
  const [paymentModal, setPaymentModal] = useState(null)
  const [payingLicenseId, setPayingLicenseId] = useState(null)
  const [assignForm, setAssignForm] = useState({
    userId: '',
    productId: '',
    startDate: toInputDate(new Date()),
    endDate: toInputDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000))
  })
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [landingPages, setLandingPages] = useState(createEmptyPaged())
  const [landingLeads, setLandingLeads] = useState(createEmptyPaged())
  const [landingVisits, setLandingVisits] = useState(createEmptyPaged())
  const [landingEvents, setLandingEvents] = useState(createEmptyPaged())
  const [landingAttributions, setLandingAttributions] = useState(createEmptyPaged())
  const [landingPageFilter, setLandingPageFilter] = useState({
    q: '',
    productId: '',
    isActive: 'all',
    page: 1,
    pageSize: 20
  })
  const [landingLeadFilter, setLandingLeadFilter] = useState({
    q: '',
    productId: '',
    isSpam: 'all',
    page: 1,
    pageSize: 20
  })
  const [landingDataFilter, setLandingDataFilter] = useState({
    productId: '',
    days: 30,
    pageSize: 10
  })
  const [landingPageForm, setLandingPageForm] = useState({
    productId: '',
    slug: '',
    localeRegion: 'latam',
    title: '',
    metaDescription: '',
    isActive: true
  })
  const [productionBackupAvailability, setProductionBackupAvailability] = useState({ available: false })
  const [productionBackupJob, setProductionBackupJob] = useState(null)
  const [productionBackupRunning, setProductionBackupRunning] = useState(false)
  const [productionBackupForm, setProductionBackupForm] = useState({ requestId: '', token: '' })
  const [productionBackupCreated, setProductionBackupCreated] = useState(null)
  const [productionBackupCreating, setProductionBackupCreating] = useState(false)

  const activeSection = resolveSection(location.pathname)
  const rolesList = user?.Roles || []
  const normalizedRoles = rolesList.map((role) => String(role || '').trim().toLowerCase())
  const canManageCatalog = normalizedRoles.includes('admin') || normalizedRoles.includes('superadmin')

  const activeUsers = useMemo(() => users.filter((entry) => entry.isActive), [users])
  const catalogProductOptions = useMemo(() => {
    if (catalogProducts.length > 0) return catalogProducts
    return products
  }, [catalogProducts, products])
  const filteredLicenses = useMemo(() => {
    const term = licenseSearch.trim().toLowerCase()
    if (!term) return licenses

    return licenses.filter((entry) => [
      entry.userName,
      entry.userEmail,
      entry.productName,
      entry.productSlug
    ].some((value) => String(value || '').toLowerCase().includes(term)))
  }, [licenses, licenseSearch])

  useEffect(() => {
    loadAll()
  }, [canManageCatalog])

  useEffect(() => {
    if (!productionBackupJob?.id) return undefined
    const timer = window.setInterval(async () => {
      try {
        const next = await adminService.getProductionBackupImportStatus(productionBackupJob.id)
        setProductionBackupJob(next)
        if (['Failed', 'Completed'].includes(next.status)) window.clearInterval(timer)
      } catch {
        window.clearInterval(timer)
      }
    }, 3000)
    return () => window.clearInterval(timer)
  }, [productionBackupJob?.id])

  useEffect(() => {
    if (activeSection === 'landingPages') {
      loadLandingPages(landingPageFilter)
    }
    if (activeSection === 'landingLeads') {
      loadLandingLeads(landingLeadFilter)
    }
    if (activeSection === 'landingData') {
      loadLandingData(landingDataFilter)
    }
  }, [activeSection])

  const loadAll = async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      const [usersRes, rolesRes, licensesRes, pendingRequestsRes, schemaRes, productsRes] = await Promise.all([
        adminService.getUsers(),
        adminService.getRoles(),
        adminService.getLicenses(),
        adminService.getLicenseRequests({ status: 'pending' }),
        adminService.getSchemaStatus(),
        adminService.getLicenseProducts()
      ])

      setUsers(usersRes || [])
      setRoles(rolesRes || [])
      setLicenses(licensesRes || [])
      setPendingLicenseRequests(pendingRequestsRes || [])
      setSchemaStatus(schemaRes || [])
      setProducts((productsRes || []).filter((entry) => entry.isActive))

      try {
        const backupAvailability = await adminService.getProductionBackupAvailability()
        setProductionBackupAvailability(backupAvailability || { available: false })
      } catch {
        setProductionBackupAvailability({ available: false })
      }

      if (canManageCatalog) {
        const [categoriesRes, catalogProductsRes] = await Promise.all([
          adminService.getCategories(),
          adminService.getProducts()
        ])
        setCategories(categoriesRes || [])
        setCatalogProducts(catalogProductsRes || [])
      } else {
        setCategories([])
        setCatalogProducts([])
      }
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible cargar la informacion administrativa.')
    } finally {
      setLoading(false)
    }
  }

  const loadLicenses = async (status = licenseFilter) => {
    const queryStatus = status === 'all' ? undefined : status === 'overdue' ? 'expired' : status
    const data = await adminService.getLicenses({ status: queryStatus })
    setLicenses(data || [])
  }

  const onAssignLicense = async (event) => {
    event.preventDefault()
    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.assignLicense({
        userId: assignForm.userId,
        productId: Number(assignForm.productId),
        startDate: new Date(assignForm.startDate).toISOString(),
        endDate: new Date(assignForm.endDate).toISOString()
      })
      setStatusMessage('Licencia asignada.')
      await loadLicenses()
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible asignar la licencia.')
    }
  }

  const onDeactivateLicense = async (licenseId) => {
    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.deactivateLicense(licenseId)
      setStatusMessage('Licencia desactivada.')
      await loadLicenses()
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible desactivar la licencia.')
    }
  }

  const onEnsureRentalBizProfile = async (license) => {
    setStatusMessage('')
    setErrorMessage('')
    setProvisioningRentalBizUserId(license.userId)
    try {
      await adminService.ensureRentalBizProfile(license.userId)
      setStatusMessage(`Perfil de RentalBiz creado para ${license.userName}.`)
      await loadLicenses()
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible crear el perfil de RentalBiz.')
    } finally {
      setProvisioningRentalBizUserId(null)
    }
  }

  const openApprovalModal = (requestItem) => {
    setPaymentModal({ mode: 'approve', item: requestItem, months: 1, note: 'Solicitud aprobada por administrador.' })
  }

  const openPaymentModal = (license) => {
    setPaymentModal({ mode: 'pay', item: license, months: 1, note: 'Pago registrado por administrador.' })
  }

  const closePaymentModal = () => {
    setPaymentModal(null)
  }

  const onConfirmPaymentModal = async () => {
    if (!paymentModal?.item) return

    setStatusMessage('')
    setErrorMessage('')
    const paidMonths = Number(paymentModal.months || 1)

    try {
      if (paymentModal.mode === 'approve') {
        const requestItem = paymentModal.item
        setApprovingRequestId(requestItem.id)
        await adminService.approveLicenseRequest(requestItem.id, {
          paidMonths,
          reviewNote: paymentModal.note
        })

        setStatusMessage(`Solicitud aprobada por ${paidMonths} mes(es) para ${requestItem.productName}.`)
        await Promise.all([
          loadLicenses(),
          adminService.getLicenseRequests({ status: 'pending' }).then((data) => setPendingLicenseRequests(data || []))
        ])
      } else {
        const license = paymentModal.item
        setPayingLicenseId(license.id)
        await adminService.payLicense(license.id, {
          paidMonths,
          note: paymentModal.note
        })

        setStatusMessage(`Pago registrado por ${paidMonths} mes(es) para ${license.productName}.`)
        await loadLicenses()
      }

      closePaymentModal()
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible registrar el pago.')
    } finally {
      setApprovingRequestId(null)
      setPayingLicenseId(null)
    }
  }

  const onToggleUser = async (currentUser) => {
    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.updateUserActive(currentUser.id, !currentUser.isActive)
      setStatusMessage('Estado de usuario actualizado.')
      const refreshed = await adminService.getUsers()
      setUsers(refreshed || [])
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible actualizar el usuario.')
    }
  }

  const onApplySchemaMigration = async (moduleKey, onlyNext) => {
    setStatusMessage('')
    setErrorMessage('')
    try {
      const result = await adminService.applySchemaMigrations(moduleKey, onlyNext)
      setStatusMessage(result.message || 'Migracion aplicada.')
      const refreshed = await adminService.getSchemaStatus()
      setSchemaStatus(refreshed || [])
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible aplicar migraciones.')
    }
  }

  const onEditUserProfile = async (currentUser) => {
    const firstName = window.prompt('Nombre', currentUser.firstName || '')
    if (firstName === null) return
    const lastName = window.prompt('Apellido', currentUser.lastName || '')
    if (lastName === null) return
    const email = window.prompt('Correo', currentUser.email || '')
    if (email === null) return

    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.updateUserProfile(currentUser.id, { firstName, lastName, email })
      setStatusMessage('Perfil actualizado.')
      const refreshed = await adminService.getUsers()
      setUsers(refreshed || [])
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible actualizar el perfil.')
    }
  }

  const onUpdateRoles = async (userId, selectedRole) => {
    setStatusMessage('')
    setErrorMessage('')
    try {
      const rolesToAssign = selectedRole ? [selectedRole] : []
      await adminService.updateUserRoles(userId, rolesToAssign)
      setStatusMessage('Roles actualizados.')
      const refreshed = await adminService.getUsers()
      setUsers(refreshed || [])
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible actualizar roles.')
    }
  }

  const normalizePagedResponse = (data, fallbackPageSize = 20) => ({
    page: data?.page || 1,
    pageSize: data?.pageSize || fallbackPageSize,
    totalItems: data?.totalItems || 0,
    items: data?.items || []
  })

  const loadLandingPages = async (filter = landingPageFilter) => {
    setErrorMessage('')
    try {
      const payload = await adminService.getLandingPages({
        q: filter.q?.trim() || undefined,
        productId: filter.productId ? Number(filter.productId) : undefined,
        isActive: filter.isActive === 'all' ? undefined : filter.isActive === 'active',
        page: filter.page,
        pageSize: filter.pageSize
      })
      setLandingPages(normalizePagedResponse(payload, filter.pageSize))
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible cargar landing pages.')
    }
  }

  const loadLandingLeads = async (filter = landingLeadFilter) => {
    setErrorMessage('')
    try {
      const payload = await adminService.getLandingLeads({
        q: filter.q?.trim() || undefined,
        productId: filter.productId ? Number(filter.productId) : undefined,
        isSpam: filter.isSpam === 'all' ? undefined : filter.isSpam === 'spam',
        page: filter.page,
        pageSize: filter.pageSize
      })
      setLandingLeads(normalizePagedResponse(payload, filter.pageSize))
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible cargar leads de landing.')
    }
  }

  const loadLandingData = async (filter = landingDataFilter) => {
    setErrorMessage('')
    try {
      const now = new Date()
      const from = new Date(now)
      from.setDate(now.getDate() - Number(filter.days || 30))

      const params = {
        productId: filter.productId ? Number(filter.productId) : undefined,
        fromUtc: from.toISOString(),
        toUtc: now.toISOString(),
        page: 1,
        pageSize: filter.pageSize
      }

      const [visitsRes, eventsRes, attributionsRes] = await Promise.all([
        adminService.getLandingVisits(params),
        adminService.getLandingEvents(params),
        adminService.getLandingAttributions(params)
      ])

      setLandingVisits(normalizePagedResponse(visitsRes, filter.pageSize))
      setLandingEvents(normalizePagedResponse(eventsRes, filter.pageSize))
      setLandingAttributions(normalizePagedResponse(attributionsRes, filter.pageSize))
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible cargar tracking de landing.')
    }
  }

  const onCreateLandingPage = async (event) => {
    event.preventDefault()
    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.createLandingPage({
        productId: Number(landingPageForm.productId),
        slug: landingPageForm.slug,
        localeRegion: landingPageForm.localeRegion,
        title: landingPageForm.title,
        metaDescription: landingPageForm.metaDescription || null,
        isActive: landingPageForm.isActive
      })
      setStatusMessage('Landing page creada.')
      setLandingPageForm((prev) => ({
        ...prev,
        slug: '',
        title: '',
        metaDescription: ''
      }))
      await loadLandingPages(landingPageFilter)
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible crear la landing page.')
    }
  }

  const onEditLandingPage = async (entry) => {
    const nextTitle = window.prompt('Titulo', entry.title || '')
    if (nextTitle === null) return
    const nextSlug = window.prompt('Slug', entry.slug || '')
    if (nextSlug === null) return
    const nextLocale = window.prompt('Region/Locale', entry.localeRegion || 'latam')
    if (nextLocale === null) return
    const nextMeta = window.prompt('Meta description', entry.metaDescription || '')
    if (nextMeta === null) return

    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.updateLandingPage(entry.id, {
        title: nextTitle,
        slug: nextSlug,
        localeRegion: nextLocale,
        metaDescription: nextMeta
      })
      setStatusMessage('Landing page actualizada.')
      await loadLandingPages(landingPageFilter)
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible actualizar la landing page.')
    }
  }

  const onToggleLandingPage = async (entry) => {
    setStatusMessage('')
    setErrorMessage('')
    try {
      await adminService.updateLandingPage(entry.id, { isActive: !entry.isActive })
      setStatusMessage('Estado de landing page actualizado.')
      await loadLandingPages(landingPageFilter)
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible actualizar el estado de la landing page.')
    }
  }

  const onLandingPageSearch = async (event) => {
    event.preventDefault()
    const nextFilter = { ...landingPageFilter, page: 1 }
    setLandingPageFilter(nextFilter)
    await loadLandingPages(nextFilter)
  }

  const onLandingLeadsSearch = async (event) => {
    event.preventDefault()
    const nextFilter = { ...landingLeadFilter, page: 1 }
    setLandingLeadFilter(nextFilter)
    await loadLandingLeads(nextFilter)
  }

  const onLandingPagesGoToPage = async (nextPage) => {
    if (nextPage < 1) return
    const lastPage = Math.max(1, Math.ceil(landingPages.totalItems / landingPages.pageSize))
    if (nextPage > lastPage) return
    const nextFilter = { ...landingPageFilter, page: nextPage }
    setLandingPageFilter(nextFilter)
    await loadLandingPages(nextFilter)
  }

  const onLandingLeadsGoToPage = async (nextPage) => {
    if (nextPage < 1) return
    const lastPage = Math.max(1, Math.ceil(landingLeads.totalItems / landingLeads.pageSize))
    if (nextPage > lastPage) return
    const nextFilter = { ...landingLeadFilter, page: nextPage }
    setLandingLeadFilter(nextFilter)
    await loadLandingLeads(nextFilter)
  }

  const onCreateProductionBackupRequest = async () => {
    setStatusMessage('')
    setErrorMessage('')
    setProductionBackupCreating(true)
    try {
      const response = await adminService.createProductionBackupRequest()
      setProductionBackupCreated(response)
      setProductionBackupForm({ requestId: response.requestId || '', token: response.token || '' })
      setStatusMessage('Solicitud creada en producción. Copia el RequestId y token; el token se muestra una sola vez.')
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible crear la solicitud de backup en producción.')
    } finally {
      setProductionBackupCreating(false)
    }
  }

  const onStartProductionBackup = async () => {
    setStatusMessage('')
    setErrorMessage('')
    setProductionBackupRunning(true)
    try {
      if (!productionBackupForm.requestId.trim() || !productionBackupForm.token.trim()) {
        setErrorMessage('Debes suministrar el RequestId y el token generado en producción.')
        return
      }
      const job = await adminService.startProductionBackupImport({
        requestId: productionBackupForm.requestId.trim(),
        token: productionBackupForm.token.trim()
      })
      setProductionBackupJob(job)
      setStatusMessage('Proceso de backup iniciado.')
    } catch (error) {
      setErrorMessage(error.message || 'No fue posible iniciar el backup de producción.')
    } finally {
      setProductionBackupRunning(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-gray-500">Cargando modulo administrativo...</div>
      </div>
    )
  }

  const visibleSections = sections

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Modulo Administrativo EcoApps</h1>
        <p className="text-sm text-gray-600">Gestion de licencias, usuarios, schema, catalogo y operaciones de landing.</p>
      </div>

      <section className="rounded-lg bg-white border border-gray-200 shadow-sm p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
          Menu Administrativo
        </p>
        <div className="flex flex-wrap gap-2">
          {visibleSections.map((section) => (
            <button
              key={section.key}
              type="button"
              onClick={() => navigate(section.path)}
              className={`px-3 py-2 rounded-md text-sm ${
                activeSection === section.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      </section>

      {statusMessage && <div className="rounded-md bg-green-50 text-green-700 px-3 py-2 text-sm">{statusMessage}</div>}
      {errorMessage && <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{errorMessage}</div>}

      {activeSection === 'licenses' && (
        <section className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Licencias activas</p>
              <p className="text-xl font-semibold">{licenses.filter((entry) => entry.isActive && !entry.isExpired).length}</p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Licencias vencidas</p>
              <p className="text-xl font-semibold">{licenses.filter((entry) => entry.isExpired).length}</p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Usuarios activos</p>
              <p className="text-xl font-semibold">{activeUsers.length}</p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Total licencias</p>
              <p className="text-xl font-semibold">{licenses.length}</p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Solicitudes pendientes</p>
              <p className="text-xl font-semibold">{pendingLicenseRequests.length}</p>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm">
            <h2 className="font-medium text-gray-900 mb-3">Asignar licencia</h2>
            <form onSubmit={onAssignLicense} className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select
                value={assignForm.userId}
                onChange={(event) => setAssignForm((prev) => ({ ...prev, userId: event.target.value }))}
                className="border rounded-md px-3 py-2"
                required
              >
                <option value="">Usuario</option>
                {users.map((entry) => (
                  <option key={entry.id} value={entry.id}>{entry.firstName} {entry.lastName} ({entry.email})</option>
                ))}
              </select>
              <select
                value={assignForm.productId}
                onChange={(event) => setAssignForm((prev) => ({ ...prev, productId: event.target.value }))}
                className="border rounded-md px-3 py-2"
                required
              >
                <option value="">Producto</option>
                {products.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name} ({entry.slug})
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={assignForm.startDate}
                onChange={(event) => setAssignForm((prev) => ({ ...prev, startDate: event.target.value }))}
                className="border rounded-md px-3 py-2"
                required
              />
              <input
                type="date"
                value={assignForm.endDate}
                onChange={(event) => setAssignForm((prev) => ({ ...prev, endDate: event.target.value }))}
                className="border rounded-md px-3 py-2"
                required
              />
              <button type="submit" className="bg-primary-600 text-white rounded-md px-3 py-2">
                Asignar
              </button>
            </form>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-gray-900">Solicitudes pendientes</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Usuario</th>
                    <th className="text-left py-2">Producto</th>
                    <th className="text-left py-2">Nota</th>
                    <th className="text-left py-2">Fecha solicitud</th>
                    <th className="text-left py-2">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingLicenseRequests.length === 0 && (
                    <tr>
                      <td className="py-3 text-gray-500" colSpan={5}>No hay solicitudes pendientes.</td>
                    </tr>
                  )}
                  {pendingLicenseRequests.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">
                        {entry.userName}
                        <br />
                        <span className="text-xs text-gray-500">{entry.userEmail}</span>
                      </td>
                      <td className="py-2">{entry.productName}</td>
                      <td className="py-2">{entry.requestNote || '-'}</td>
                      <td className="py-2">{toInputDate(entry.requestedAt)}</td>
                      <td className="py-2">
                        <button
                          type="button"
                          className="px-3 py-1 rounded-md bg-primary-600 text-white disabled:opacity-60"
                          onClick={() => openApprovalModal(entry)}
                          disabled={approvingRequestId === entry.id}
                        >
                          {approvingRequestId === entry.id ? 'Aprobando...' : 'Aprobar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-gray-900">Licencias</h2>
              <div className="flex flex-wrap gap-2">
                <input
                  value={licenseSearch}
                  onChange={(event) => setLicenseSearch(event.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                  placeholder="Buscar vencidas por usuario o producto"
                />
                <select
                  value={licenseFilter}
                  onChange={async (event) => {
                    const next = event.target.value
                    setLicenseFilter(next)
                    await loadLicenses(next)
                  }}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">Todas</option>
                  <option value="active">Activas</option>
                  <option value="expired">Vencidas</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Usuario</th>
                    <th className="text-left py-2">Producto</th>
                    <th className="text-left py-2">Inicio</th>
                    <th className="text-left py-2">Fin</th>
                    <th className="text-left py-2">Estado</th>
                    <th className="text-left py-2">Perfil RentalBiz</th>
                    <th className="text-left py-2">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLicenses.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{entry.userName}<br /><span className="text-xs text-gray-500">{entry.userEmail}</span></td>
                      <td className="py-2">{entry.productName}</td>
                      <td className="py-2">{toInputDate(entry.licenseStartDate)}</td>
                      <td className="py-2">{toInputDate(entry.licenseEndDate)}</td>
                      <td className="py-2">
                        {entry.isExpired ? 'Vencida' : (entry.isActive ? 'Activa' : 'Inactiva')}
                      </td>
                      <td className="py-2">
                        {String(entry.productSlug || '').toLowerCase() === 'rentalbiz'
                          ? (entry.hasRentalBizProfile === true ? 'Creado' : entry.hasRentalBizProfile === false ? 'Faltante' : 'Verificando')
                          : '-'}
                      </td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-3">
                          {String(entry.productSlug || '').toLowerCase() === 'rentalbiz' &&
                            entry.isActive &&
                            !entry.isExpired &&
                            entry.hasRentalBizProfile === false && (
                            <button
                              type="button"
                              className="text-primary-700"
                              onClick={() => onEnsureRentalBizProfile(entry)}
                              disabled={provisioningRentalBizUserId === entry.userId}
                            >
                              {provisioningRentalBizUserId === entry.userId ? 'Creando...' : 'Crear perfil RentalBiz'}
                            </button>
                          )}
                          {entry.isActive && (
                            <button
                              type="button"
                              className="text-red-600"
                              onClick={() => onDeactivateLicense(entry.id)}
                            >
                              Desactivar
                            </button>
                          )}
                          {entry.isExpired && (
                            <button
                              type="button"
                              className="text-green-700 disabled:opacity-60"
                              onClick={() => openPaymentModal(entry)}
                              disabled={payingLicenseId === entry.id}
                            >
                              {payingLicenseId === entry.id ? 'Pagando...' : 'Pagar'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {paymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {paymentModal.mode === 'approve' ? 'Aprobar licencia' : 'Registrar pago'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {paymentModal.item.userName || paymentModal.item.userEmail} - {paymentModal.item.productName}
              </p>
            </div>

            <label className="block text-sm">
              <span className="text-gray-700">Meses pagados</span>
              <select
                value={paymentModal.months}
                onChange={(event) => setPaymentModal((current) => ({ ...current, months: Number(event.target.value) }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              >
                {monthOptions.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="text-gray-700">Nota</span>
              <textarea
                value={paymentModal.note}
                onChange={(event) => setPaymentModal((current) => ({ ...current, note: event.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                rows={3}
              />
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border px-3 py-2 text-sm"
                onClick={closePaymentModal}
                disabled={Boolean(approvingRequestId || payingLicenseId)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-md bg-primary-600 px-3 py-2 text-sm text-white disabled:opacity-60"
                onClick={onConfirmPaymentModal}
                disabled={Boolean(approvingRequestId || payingLicenseId)}
              >
                {paymentModal.mode === 'approve'
                  ? (approvingRequestId ? 'Aprobando...' : 'Aceptar pago y aprobar')
                  : (payingLicenseId ? 'Registrando...' : 'Aceptar pago')}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'schema' && (
        <section className="space-y-4">
          {schemaStatus.map((module) => (
            <div key={module.moduleKey} className="rounded-lg bg-white p-4 shadow-sm space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="font-medium text-gray-900">{module.moduleName}</h2>
                  <p className="text-xs text-gray-600">
                    Pendientes: {module.pendingMigrations.length} | Aplicadas: {module.appliedMigrations.length}
                  </p>
                  {module.hasDestructivePendingChanges && (
                    <p className="text-xs text-red-600">Hay cambios destructivos pendientes y seran bloqueados por seguridad.</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-md bg-gray-900 text-white text-sm"
                    onClick={() => onApplySchemaMigration(module.moduleKey, true)}
                    disabled={module.pendingMigrations.length === 0}
                  >
                    Migrar siguiente
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-md bg-primary-600 text-white text-sm"
                    onClick={() => onApplySchemaMigration(module.moduleKey, false)}
                    disabled={module.pendingMigrations.length === 0}
                  >
                    Migrar todo incremental
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Tabla</th>
                      <th className="text-left py-2">Existe</th>
                      <th className="text-left py-2">Filas</th>
                      <th className="text-left py-2">Campos faltantes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {module.tables.map((table) => (
                      <tr key={`${table.schema}.${table.tableName}`} className="border-b">
                        <td className="py-2">{table.schema}.{table.tableName}</td>
                        <td className="py-2">{table.exists ? 'Si' : 'No'}</td>
                        <td className="py-2">{table.rowCount}</td>
                        <td className="py-2">
                          {table.missingColumns.length > 0 ? table.missingColumns.join(', ') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>
      )}

      {activeSection === 'users' && (
        <section className="rounded-lg bg-white p-4 shadow-sm space-y-3">
          <h2 className="font-medium text-gray-900">Gestion de usuarios</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Nombre</th>
                  <th className="text-left py-2">Correo</th>
                  <th className="text-left py-2">Activo</th>
                  <th className="text-left py-2">Rol</th>
                  <th className="text-left py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((entry) => (
                  <tr key={entry.id} className="border-b">
                    <td className="py-2">{entry.firstName} {entry.lastName}</td>
                    <td className="py-2">{entry.email}</td>
                    <td className="py-2">{entry.isActive ? 'Si' : 'No'}</td>
                    <td className="py-2">
                      <select
                        className="border rounded-md px-2 py-1"
                        defaultValue={entry.roles?.[0] || ''}
                        onChange={(event) => onUpdateRoles(entry.id, event.target.value)}
                      >
                        <option value="">Sin rol</option>
                        {roles.map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => onEditUserProfile(entry)}
                          className="px-3 py-1 rounded-md text-white bg-gray-700"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleUser(entry)}
                          className={`px-3 py-1 rounded-md text-white ${entry.isActive ? 'bg-red-600' : 'bg-green-600'}`}
                        >
                          {entry.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeSection === 'categories' && (
        canManageCatalog ? (
          <section className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h2 className="font-medium text-gray-900">Categorias</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Id</th>
                    <th className="text-left py-2">Nombre</th>
                    <th className="text-left py-2">Slug</th>
                    <th className="text-left py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{entry.id}</td>
                      <td className="py-2">{entry.name}</td>
                      <td className="py-2">{entry.slug}</td>
                      <td className="py-2">{entry.isActive ? 'Activa' : 'Inactiva'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null
      )}

      {activeSection === 'products' && (
        canManageCatalog ? (
          <section className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h2 className="font-medium text-gray-900">Productos</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Id</th>
                    <th className="text-left py-2">Nombre</th>
                    <th className="text-left py-2">Slug</th>
                    <th className="text-left py-2">Categoria</th>
                    <th className="text-left py-2">Licencia</th>
                    <th className="text-left py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogProducts.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{entry.id}</td>
                      <td className="py-2">{entry.name}</td>
                      <td className="py-2">{entry.slug}</td>
                      <td className="py-2">{entry.category?.name || '-'}</td>
                      <td className="py-2">{entry.requiresLicense ? 'Si' : 'No'}</td>
                      <td className="py-2">{entry.isActive ? 'Activo' : 'Inactivo'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null
      )}

      {activeSection === 'landingPages' && (
        <section className="space-y-4">
          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h2 className="font-medium text-gray-900">Gestion de Landing Pages</h2>
            <form onSubmit={onLandingPageSearch} className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select
                value={landingPageFilter.productId}
                onChange={(event) => setLandingPageFilter((prev) => ({ ...prev, productId: event.target.value }))}
                className="border rounded-md px-3 py-2"
              >
                <option value="">Todos los productos</option>
                {catalogProductOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name} ({entry.slug})
                  </option>
                ))}
              </select>
              <select
                value={landingPageFilter.isActive}
                onChange={(event) => setLandingPageFilter((prev) => ({ ...prev, isActive: event.target.value }))}
                className="border rounded-md px-3 py-2"
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activas</option>
                <option value="inactive">Inactivas</option>
              </select>
              <input
                value={landingPageFilter.q}
                onChange={(event) => setLandingPageFilter((prev) => ({ ...prev, q: event.target.value }))}
                className="border rounded-md px-3 py-2 md:col-span-2"
                placeholder="Buscar por slug, titulo o region"
              />
              <button type="submit" className="bg-primary-600 text-white rounded-md px-3 py-2">
                Buscar
              </button>
            </form>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h3 className="font-medium text-gray-900">Crear landing page</h3>
            <form onSubmit={onCreateLandingPage} className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <select
                value={landingPageForm.productId}
                onChange={(event) => setLandingPageForm((prev) => ({ ...prev, productId: event.target.value }))}
                className="border rounded-md px-3 py-2"
                required
              >
                <option value="">Producto</option>
                {catalogProductOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name} ({entry.slug})
                  </option>
                ))}
              </select>
              <input
                value={landingPageForm.slug}
                onChange={(event) => setLandingPageForm((prev) => ({ ...prev, slug: event.target.value }))}
                className="border rounded-md px-3 py-2"
                placeholder="/latam/soluciones/..."
                required
              />
              <input
                value={landingPageForm.localeRegion}
                onChange={(event) => setLandingPageForm((prev) => ({ ...prev, localeRegion: event.target.value }))}
                className="border rounded-md px-3 py-2"
                placeholder="latam"
              />
              <input
                value={landingPageForm.title}
                onChange={(event) => setLandingPageForm((prev) => ({ ...prev, title: event.target.value }))}
                className="border rounded-md px-3 py-2"
                placeholder="Titulo SEO"
                required
              />
              <input
                value={landingPageForm.metaDescription}
                onChange={(event) => setLandingPageForm((prev) => ({ ...prev, metaDescription: event.target.value }))}
                className="border rounded-md px-3 py-2"
                placeholder="Meta description"
              />
              <button type="submit" className="bg-primary-600 text-white rounded-md px-3 py-2">
                Crear
              </button>
            </form>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-medium text-gray-900">Landing Pages</h3>
              <p className="text-xs text-gray-600">Total: {landingPages.totalItems}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Slug</th>
                    <th className="text-left py-2">Producto</th>
                    <th className="text-left py-2">Region</th>
                    <th className="text-left py-2">Estado</th>
                    <th className="text-left py-2">Visitas</th>
                    <th className="text-left py-2">Eventos</th>
                    <th className="text-left py-2">Leads</th>
                    <th className="text-left py-2">Actualizado</th>
                    <th className="text-left py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {landingPages.items.length === 0 && (
                    <tr>
                      <td className="py-3 text-gray-500" colSpan={9}>No hay landing pages para los filtros seleccionados.</td>
                    </tr>
                  )}
                  {landingPages.items.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{entry.slug}</td>
                      <td className="py-2">{entry.productName}</td>
                      <td className="py-2">{entry.localeRegion}</td>
                      <td className="py-2">{entry.isActive ? 'Activa' : 'Inactiva'}</td>
                      <td className="py-2">{entry.visits}</td>
                      <td className="py-2">{entry.events}</td>
                      <td className="py-2">{entry.leads}</td>
                      <td className="py-2">{toDateTimeLabel(entry.updatedAt || entry.createdAt)}</td>
                      <td className="py-2">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => onEditLandingPage(entry)}
                            className="px-3 py-1 rounded-md text-white bg-gray-700"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => onToggleLandingPage(entry)}
                            className={`px-3 py-1 rounded-md text-white ${entry.isActive ? 'bg-red-600' : 'bg-green-600'}`}
                          >
                            {entry.isActive ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 rounded-md border"
                onClick={() => onLandingPagesGoToPage(landingPages.page - 1)}
                disabled={landingPages.page <= 1}
              >
                Anterior
              </button>
              <span className="text-xs text-gray-600">
                Pagina {landingPages.page} de {Math.max(1, Math.ceil(landingPages.totalItems / landingPages.pageSize))}
              </span>
              <button
                type="button"
                className="px-3 py-1 rounded-md border"
                onClick={() => onLandingPagesGoToPage(landingPages.page + 1)}
                disabled={landingPages.page >= Math.max(1, Math.ceil(landingPages.totalItems / landingPages.pageSize))}
              >
                Siguiente
              </button>
            </div>
          </div>
        </section>
      )}

      {activeSection === 'landingLeads' && (
        <section className="space-y-4">
          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h2 className="font-medium text-gray-900">Leads de Landing</h2>
            <form onSubmit={onLandingLeadsSearch} className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select
                value={landingLeadFilter.productId}
                onChange={(event) => setLandingLeadFilter((prev) => ({ ...prev, productId: event.target.value }))}
                className="border rounded-md px-3 py-2"
              >
                <option value="">Todos los productos</option>
                {catalogProductOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name} ({entry.slug})
                  </option>
                ))}
              </select>
              <select
                value={landingLeadFilter.isSpam}
                onChange={(event) => setLandingLeadFilter((prev) => ({ ...prev, isSpam: event.target.value }))}
                className="border rounded-md px-3 py-2"
              >
                <option value="all">Todos</option>
                <option value="valid">No spam</option>
                <option value="spam">Spam</option>
              </select>
              <input
                value={landingLeadFilter.q}
                onChange={(event) => setLandingLeadFilter((prev) => ({ ...prev, q: event.target.value }))}
                className="border rounded-md px-3 py-2 md:col-span-2"
                placeholder="Buscar por nombre, correo, empresa o telefono"
              />
              <button type="submit" className="bg-primary-600 text-white rounded-md px-3 py-2">
                Buscar
              </button>
            </form>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Listado de leads</h3>
              <p className="text-xs text-gray-600">Total: {landingLeads.totalItems}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Fecha</th>
                    <th className="text-left py-2">Lead</th>
                    <th className="text-left py-2">Producto</th>
                    <th className="text-left py-2">Landing</th>
                    <th className="text-left py-2">Pais/Rol</th>
                    <th className="text-left py-2">UTM Campaign</th>
                    <th className="text-left py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {landingLeads.items.length === 0 && (
                    <tr>
                      <td className="py-3 text-gray-500" colSpan={7}>No hay leads para los filtros seleccionados.</td>
                    </tr>
                  )}
                  {landingLeads.items.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{toDateTimeLabel(entry.createdAt)}</td>
                      <td className="py-2">
                        <div>{entry.name}</div>
                        <div className="text-xs text-gray-500">{entry.email}</div>
                        {entry.company ? <div className="text-xs text-gray-500">{entry.company}</div> : null}
                      </td>
                      <td className="py-2">{entry.productName || '-'}</td>
                      <td className="py-2">{entry.landingSlug || '-'}</td>
                      <td className="py-2">
                        {entry.country || '-'}
                        <br />
                        <span className="text-xs text-gray-500">{entry.role || '-'}</span>
                      </td>
                      <td className="py-2">{entry.utmCampaign || '-'}</td>
                      <td className="py-2">
                        {entry.isSpam ? 'Spam' : 'Valido'}
                        <br />
                        <span className="text-xs text-gray-500">{entry.confirmationEmailSent ? 'Confirmado' : 'Sin confirmacion'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 rounded-md border"
                onClick={() => onLandingLeadsGoToPage(landingLeads.page - 1)}
                disabled={landingLeads.page <= 1}
              >
                Anterior
              </button>
              <span className="text-xs text-gray-600">
                Pagina {landingLeads.page} de {Math.max(1, Math.ceil(landingLeads.totalItems / landingLeads.pageSize))}
              </span>
              <button
                type="button"
                className="px-3 py-1 rounded-md border"
                onClick={() => onLandingLeadsGoToPage(landingLeads.page + 1)}
                disabled={landingLeads.page >= Math.max(1, Math.ceil(landingLeads.totalItems / landingLeads.pageSize))}
              >
                Siguiente
              </button>
            </div>
          </div>
        </section>
      )}

      {activeSection === 'productionBackup' && (
        <section className="rounded-lg bg-white p-4 shadow-sm space-y-4">
          <div>
            <h2 className="font-medium text-gray-900">Crear backup de producción</h2>
            <p className="text-sm text-gray-600 mt-1">
              En producción genera el RequestId/token de un solo uso. En desarrollo permite pegar esos datos para descargar/restaurar localmente con firma criptográfica de máquina.
            </p>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            El token expira y se consume en el primer intento de descarga válida. Un segundo uso debe responder 401 Unauthorized.
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onCreateProductionBackupRequest}
                disabled={productionBackupCreating}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {productionBackupCreating ? 'Generando...' : 'Generar token en producción'}
              </button>
              <span className="text-xs text-gray-500">Usar este botón en producción como admin autorizado.</span>
            </div>
            {productionBackupCreated && (
              <div className="rounded-md bg-white p-3 text-sm border border-emerald-200">
                <p><strong>RequestId:</strong> {productionBackupCreated.requestId}</p>
                <p className="break-all"><strong>Token:</strong> {productionBackupCreated.token}</p>
                <p><strong>Expira UTC:</strong> {productionBackupCreated.expiresAtUtc}</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">RequestId generado en producción</span>
              <input
                type="text"
                value={productionBackupForm.requestId}
                onChange={(event) => setProductionBackupForm((current) => ({ ...current, requestId: event.target.value }))}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">Token temporal de un solo uso</span>
              <input
                type="password"
                value={productionBackupForm.token}
                onChange={(event) => setProductionBackupForm((current) => ({ ...current, token: event.target.value }))}
                placeholder="Pega aquí el token generado en producción"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onStartProductionBackup}
              disabled={productionBackupRunning}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {productionBackupRunning ? 'Iniciando...' : 'Hacer backup de producción'}
            </button>
            <span className="text-xs text-gray-500">Genera token, prepara backup remoto y restaura local con nombre fechado.</span>
          </div>
          {productionBackupJob && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-gray-900">Estado: {productionBackupJob.status}</span>
                <span className="text-gray-600">{productionBackupJob.progressPercent}%</span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                <div className="h-2 rounded-full bg-indigo-600" style={{ width: `${productionBackupJob.progressPercent || 0}%` }} />
              </div>
              {productionBackupJob.message && <p className="mt-2 text-gray-600">{productionBackupJob.message}</p>}
              {productionBackupJob.remoteStatus && <p className="mt-1 text-gray-500">Estado remoto: {productionBackupJob.remoteStatus}</p>}
              {productionBackupJob.downloadedBackupPath && <p className="mt-1 break-all text-gray-500">Archivo local: {productionBackupJob.downloadedBackupPath}</p>}
              {productionBackupJob.localDatabaseName && <p className="mt-1 break-all text-gray-500">Base local: {productionBackupJob.localDatabaseName}</p>}
              {productionBackupJob.errorMessage && <p className="mt-2 text-red-600">{productionBackupJob.errorMessage}</p>}
            </div>
          )}

          <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-md bg-gray-50 p-3">
              <dt className="text-gray-500">Producción</dt>
              <dd className="font-medium text-gray-900">{productionBackupAvailability.productionBaseUrl}</dd>
            </div>
            <div className="rounded-md bg-gray-50 p-3">
              <dt className="text-gray-500">Prefijo local</dt>
              <dd className="font-medium text-gray-900">{productionBackupAvailability.localDatabasePrefix}</dd>
            </div>
            <div className="rounded-md bg-gray-50 p-3">
              <dt className="text-gray-500">Ambiente</dt>
              <dd className="font-medium text-gray-900">{productionBackupAvailability.environment}</dd>
            </div>
          </dl>
        </section>
      )}

      {activeSection === 'landingData' && (
        <section className="space-y-4">
          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h2 className="font-medium text-gray-900">Tracking Landing (solo lectura)</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <select
                value={landingDataFilter.productId}
                onChange={(event) => setLandingDataFilter((prev) => ({ ...prev, productId: event.target.value }))}
                className="border rounded-md px-3 py-2"
              >
                <option value="">Todos los productos</option>
                {catalogProductOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name} ({entry.slug})
                  </option>
                ))}
              </select>
              <select
                value={landingDataFilter.days}
                onChange={(event) => setLandingDataFilter((prev) => ({ ...prev, days: Number(event.target.value) }))}
                className="border rounded-md px-3 py-2"
              >
                <option value={7}>Ultimos 7 dias</option>
                <option value={30}>Ultimos 30 dias</option>
                <option value={90}>Ultimos 90 dias</option>
              </select>
              <button
                type="button"
                className="bg-primary-600 text-white rounded-md px-3 py-2"
                onClick={() => loadLandingData(landingDataFilter)}
              >
                Actualizar
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Visitas</p>
              <p className="text-2xl font-semibold">{landingVisits.totalItems}</p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Eventos</p>
              <p className="text-2xl font-semibold">{landingEvents.totalItems}</p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="text-xs text-gray-500">Atribuciones</p>
              <p className="text-2xl font-semibold">{landingAttributions.totalItems}</p>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h3 className="font-medium text-gray-900">Ultimas visitas</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Fecha</th>
                    <th className="text-left py-2">Landing</th>
                    <th className="text-left py-2">Producto</th>
                    <th className="text-left py-2">Session</th>
                    <th className="text-left py-2">Pais</th>
                    <th className="text-left py-2">Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {landingVisits.items.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{toDateTimeLabel(entry.visitedAt)}</td>
                      <td className="py-2">{entry.landingSlug}</td>
                      <td className="py-2">{entry.productName}</td>
                      <td className="py-2">{entry.sessionId}</td>
                      <td className="py-2">{entry.country || '-'}</td>
                      <td className="py-2">{entry.utmCampaign || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h3 className="font-medium text-gray-900">Ultimos eventos</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Fecha</th>
                    <th className="text-left py-2">Evento</th>
                    <th className="text-left py-2">Landing</th>
                    <th className="text-left py-2">Session</th>
                    <th className="text-left py-2">Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {landingEvents.items.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{toDateTimeLabel(entry.eventAt)}</td>
                      <td className="py-2">{entry.eventName}</td>
                      <td className="py-2">{entry.landingSlug}</td>
                      <td className="py-2">{entry.sessionId}</td>
                      <td className="py-2">{entry.utmCampaign || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm space-y-3">
            <h3 className="font-medium text-gray-900">Ultimas atribuciones</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Fecha</th>
                    <th className="text-left py-2">Lead</th>
                    <th className="text-left py-2">Modelo</th>
                    <th className="text-left py-2">Landing</th>
                    <th className="text-left py-2">Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {landingAttributions.items.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">{toDateTimeLabel(entry.createdAt)}</td>
                      <td className="py-2">{entry.leadEmail}</td>
                      <td className="py-2">{entry.attributionModel}</td>
                      <td className="py-2">{entry.landingSlug || '-'}</td>
                      <td className="py-2">{entry.utmCampaign || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
